import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import {
  UserListItem,
  UserDetail,
  RoleType,
  PermissionType,
  UsersFilterType,
} from "../../types/users";

type BranchListItem = {
  id: number;
  name: string;
  code: string;
  address: string | null;
};

export interface UsersState {
  filter: UsersFilterType;
  users: UserListItem[];
  totalUsers: number;
  user: UserDetail | null;
  roles: RoleType[];
  permissions: PermissionType[];
  allBranches: BranchListItem[];
  loading: boolean;
  error: string | null;
}

const initialState: UsersState = {
  filter: {
    search: null,
    sortBy: "id",
    sortOrder: "desc",
  },
  users: [],
  totalUsers: 0,
  user: null,
  roles: [],
  permissions: [],
  allBranches: [],
  loading: false,
  error: null,
};

type FetchUsersListDataProps = {
  page: number;
  size?: number;
  search?: string | null;
  sortBy?: string;
  sortOrder?: string;
};

export const fetchUsersListData = createAsyncThunk(
  "users/fetchList",
  async (req: FetchUsersListDataProps) => {
    let response: any;
    await GET(`/v1/users`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchUserData = createAsyncThunk(
  "users/fetchUser",
  async (req: { userId: number }) => {
    let response: any;
    await GET(`/v1/users/${req.userId}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchRoleData = createAsyncThunk(
  "users/fetchRoles",
  async () => {
    let response: any;
    await GET(`/v1/roles`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchPermissionData = createAsyncThunk(
  "users/fetchPermissions",
  async () => {
    let response: any;
    await GET(`/v1/permissions`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchBranchesData = createAsyncThunk(
  "users/fetchBranches",
  async () => {
    let response: any;
    await GET(`/v1/branches`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<Partial<UsersFilterType>>) => {
      state.filter = { ...state.filter, ...action.payload };
    },
    clearUser: (state) => {
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch users list
    builder.addCase(fetchUsersListData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUsersListData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.users = action.payload.data.data.records;
        state.totalUsers = action.payload.data.data.total_records;
        state.error = null;
      } else {
        state.users = [];
        state.error =
          action?.payload?.data?.message ||
          "Something went wrong. Please try again.";
      }
    });
    builder.addCase(fetchUsersListData.rejected, (state, action: any) => {
      state.users = [];
      state.loading = false;
      state.error =
        action?.payload?.data?.message ||
        "Something went wrong. Please try again.";
    });

    // Fetch single user
    builder.addCase(fetchUserData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUserData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.user = action.payload.data.data;
        state.error = null;
      } else {
        state.user = null;
        state.error =
          action?.payload?.data?.message ||
          "Something went wrong. Please try again.";
      }
    });
    builder.addCase(fetchUserData.rejected, (state, action: any) => {
      state.user = null;
      state.loading = false;
      state.error =
        action?.payload?.data?.message ||
        "Something went wrong. Please try again.";
    });

    // Fetch roles
    builder.addCase(fetchRoleData.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.roles = action.payload.data.data;
      }
    });

    // Fetch permissions
    builder.addCase(fetchPermissionData.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.permissions = action.payload.data.data;
      }
    });

    // Fetch branches
    builder.addCase(fetchBranchesData.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.allBranches = action.payload.data.data;
      }
    });
  },
});

export const { setFilter, clearUser } = usersSlice.actions;

export const getFilter = (state: RootState) => state.users.filter;
export const getUsers = (state: RootState) => state.users.users;
export const getTotalUsers = (state: RootState) => state.users.totalUsers;
export const getUser = (state: RootState) => state.users.user;
export const getRoles = (state: RootState) => state.users.roles;
export const getPermissions = (state: RootState) => state.users.permissions;
export const getAllBranches = (state: RootState) => state.users.allBranches;
export const getUsersLoading = (state: RootState) => state.users.loading;

export default usersSlice.reducer;
